import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useRealtime } from "../lib/RealtimeContext";
import { ArrowLeft, Send, Mountain } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

export default function MessageThread() {
    const { threadId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { subscribe, connected } = useRealtime();
    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [typingUntil, setTypingUntil] = useState(0);
    const listRef = useRef(null);
    const pollRef = useRef(null);
    const typingSentAt = useRef(0);
    const typingClearTimer = useRef(null);

    const scrollToBottom = () => {
        requestAnimationFrame(() => {
            if (listRef.current) {
                listRef.current.scrollTop = listRef.current.scrollHeight;
            }
        });
    };

    const fetchThread = useCallback(async () => {
        try {
            const { data } = await api.get(`/messages/threads/${threadId}`);
            setThread({ id: data.id, other_user: data.other_user });
            setMessages(data.messages);
            // mark as read (fire and forget)
            api.post(`/messages/threads/${threadId}/read`).catch(() => {});
        } catch (e) {
            toast.error(formatApiError(e));
            navigate("/messages");
        } finally {
            setLoading(false);
        }
    }, [threadId, navigate]);

    useEffect(() => {
        fetchThread();
    }, [fetchThread]);

    // Polling is a fallback when the websocket is down. When connected we
    // rely entirely on push events and skip polling.
    useEffect(() => {
        if (connected) return undefined;
        pollRef.current = setInterval(fetchThread, 8000);
        return () => clearInterval(pollRef.current);
    }, [fetchThread, connected]);

    // Realtime — append incoming messages for this thread live.
    useEffect(() => {
        const off = subscribe("message:new", ({ thread_id, message }) => {
            if (thread_id !== threadId || !message) return;
            if (message.sender_id === user?.user_id) return; // we add our own optimistically
            setMessages((xs) => (xs.some((m) => m.id === message.id) ? xs : [...xs, message]));
            // Mark as read since the thread is open.
            api.post(`/messages/threads/${threadId}/read`).catch(() => {});
        });
        return off;
    }, [subscribe, threadId, user]);

    // Typing indicator — the other side is typing if we recently got an event.
    useEffect(() => {
        const off = subscribe("message:typing", ({ thread_id, user_id }) => {
            if (thread_id !== threadId) return;
            if (user_id === user?.user_id) return;
            setTypingUntil(Date.now() + 3500);
            clearTimeout(typingClearTimer.current);
            typingClearTimer.current = setTimeout(
                () => setTypingUntil(0),
                3500,
            );
        });
        return () => {
            off();
            clearTimeout(typingClearTimer.current);
        };
    }, [subscribe, threadId, user]);

    const notifyTyping = () => {
        // Fire at most once per 2.5s.
        const nowMs = Date.now();
        if (nowMs - typingSentAt.current < 2500) return;
        typingSentAt.current = nowMs;
        api.post(`/messages/threads/${threadId}/typing`).catch(() => {});
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages.length]);

    const send = async (e) => {
        e?.preventDefault();
        const text = draft.trim();
        if (!text || sending) return;
        setSending(true);
        const optimistic = {
            id: `tmp_${Date.now()}`,
            thread_id: threadId,
            sender_id: user?.user_id,
            text,
            created_at: new Date().toISOString(),
            read_by: [user?.user_id],
            _optimistic: true,
        };
        setMessages((xs) => [...xs, optimistic]);
        setDraft("");
        try {
            const { data } = await api.post(`/messages/threads/${threadId}`, { text });
            setMessages((xs) => xs.map((m) => (m.id === optimistic.id ? data : m)));
        } catch (err) {
            toast.error(formatApiError(err));
            setMessages((xs) => xs.filter((m) => m.id !== optimistic.id));
            setDraft(text); // restore
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-ember-orange border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-4rem)] lg:h-screen flex flex-col bg-alpine-mist">
            <header className="sticky top-0 z-10 bg-snowcap border-b border-ridge-line px-4 py-3 flex items-center gap-3">
                <Link
                    to="/messages"
                    data-testid="thread-back"
                    className="w-9 h-9 rounded-md flex items-center justify-center text-granite hover:bg-alpine-mist transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Link>
                <Link
                    to={`/profile/${thread.other_user.user_id}`}
                    className="flex items-center gap-3 flex-1 min-w-0"
                >
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-trail-tan shrink-0 flex items-center justify-center">
                        {thread.other_user.avatar ? (
                            <img
                                src={thread.other_user.avatar}
                                alt={thread.other_user.name}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <Mountain className="w-5 h-5 text-midnight-pine" />
                        )}
                    </div>
                    <div className="min-w-0">
                        <div className="font-bold text-midnight-pine truncate" data-testid="thread-other-name">
                            {thread.other_user.name}
                        </div>
                        {thread.other_user.tier && (
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray capitalize">
                                {thread.other_user.tier}
                            </div>
                        )}
                    </div>
                </Link>
            </header>

            <div
                ref={listRef}
                data-testid="thread-messages"
                className="flex-1 overflow-y-auto px-4 py-6"
            >
                {messages.length === 0 ? (
                    <div className="text-center text-sm text-slate-gray mt-12">
                        Say hello — no messages yet.
                    </div>
                ) : (
                    <ul className="max-w-2xl mx-auto space-y-3">
                        {messages.map((m) => {
                            const mine = m.sender_id === user?.user_id;
                            return (
                                <li
                                    key={m.id}
                                    data-testid={`thread-message-${m.id}`}
                                    className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                >
                                    <div
                                        className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-snug ${
                                            mine
                                                ? "bg-ember-orange text-white rounded-br-md"
                                                : "bg-snowcap border border-ridge-line text-granite rounded-bl-md"
                                        }`}
                                    >
                                        <p className="whitespace-pre-wrap break-words">{m.text}</p>
                                        <div
                                            className={`mt-1 text-[10px] font-bold uppercase tracking-widest ${
                                                mine ? "text-white/70" : "text-slate-gray"
                                            }`}
                                        >
                                            {format(new Date(m.created_at), "p")}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                        {typingUntil > Date.now() && (
                            <li
                                data-testid="thread-typing-indicator"
                                className="flex justify-start"
                            >
                                <div className="max-w-[75%] px-4 py-2.5 rounded-2xl rounded-bl-md bg-snowcap border border-ridge-line">
                                    <TypingDots />
                                </div>
                            </li>
                        )}
                    </ul>
                )}
            </div>

            <form
                onSubmit={send}
                data-testid="thread-composer"
                className="sticky bottom-16 lg:bottom-0 bg-snowcap border-t border-ridge-line px-4 py-3 flex items-center gap-2"
            >
                <input
                    type="text"
                    value={draft}
                    onChange={(e) => {
                        setDraft(e.target.value);
                        if (e.target.value.trim()) notifyTyping();
                    }}
                    placeholder="Write a message…"
                    data-testid="thread-input"
                    className="flex-1 px-4 py-2.5 rounded-full bg-alpine-mist border border-ridge-line text-granite placeholder:text-slate-gray focus:outline-none focus:border-ember-orange transition-colors"
                />
                <button
                    type="submit"
                    disabled={!draft.trim() || sending}
                    data-testid="thread-send"
                    className="w-11 h-11 shrink-0 rounded-full bg-ember-orange text-white flex items-center justify-center hover:bg-ember-dark active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                    <Send className="w-5 h-5" />
                </button>
            </form>
        </div>
    );
}

function TypingDots() {
    return (
        <div className="flex items-center gap-1 h-4">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-gray animate-bounce [animation-delay:-0.3s]" />
            <span className="w-1.5 h-1.5 rounded-full bg-slate-gray animate-bounce [animation-delay:-0.15s]" />
            <span className="w-1.5 h-1.5 rounded-full bg-slate-gray animate-bounce" />
        </div>
    );
}
